// Kırılım kod/etiket yardımcıları (spec §3.1 grup kodu, §6.4 "Kırılıma dönüştür", R3 builder
// kararı) — saf, DB/FE bağımsız. Katalog modalı, dönüştürme ve kod yeniden adlandırma aynı
// `breakdownGroupCode` kuralını çağırır.

import type { KpiBreakdown } from "../schemas/kpi.js";
import { KPI_CODE_MAX_LENGTH, slugifyKpiCode } from "./kpi-code.js";
import { parseFormula } from "./kpi-formula-parser.js";
import { normalizeUnitKey } from "./kpi-units.js";

const SEP = "__";

function trimCode(code: string, max: number): string {
  return code.length <= max ? code : code.slice(0, max).replace(/_+$/, "");
}

/**
 * Grup kodu `<üst>__<boyut>` (R3): boyut slug'lanır, çift alt çizgi KORUNUR (üst kod slug'dan
 * geçmez); toplam 64'ü aşarsa üst kod kısaltılır (sondaki `_` atılır); `existing`'te varsa
 * `_2`, `_3` … soneki (yine 64 içinde).
 */
export function breakdownGroupCode(
  parentCode: string,
  dimension: string,
  existing?: ReadonlySet<string>,
): string {
  const dim = slugifyKpiCode(dimension);
  const build = (suffix: string): string =>
    trimCode(parentCode, KPI_CODE_MAX_LENGTH - SEP.length - dim.length - suffix.length) +
    SEP +
    dim +
    suffix;
  const first = build("");
  if (!existing?.has(first)) return first;
  for (let n = 2; ; n += 1) {
    const candidate = build(`_${n.toString()}`);
    if (!existing.has(candidate)) return candidate;
  }
}

/** Kod bu üst + boyut için türetilmiş grup kodu mu (kısaltılmış ve `_n` sonekli biçimler dahil). */
export function isBreakdownGroupCodeOf(
  code: string,
  parentCode: string,
  dimension: string,
): boolean {
  const dim = slugifyKpiCode(dimension);
  const marker = `${SEP}${dim}`;
  const at = code.lastIndexOf(marker);
  if (at <= 0) return false;
  const tail = code.slice(at + marker.length);
  if (tail !== "" && !/^_\d+$/.test(tail)) return false;
  const max = KPI_CODE_MAX_LENGTH - SEP.length - dim.length - tail.length;
  return code.slice(0, at) === trimCode(parentCode, max);
}

const SEPARATORS_RE = /^[\s\-–—:›,/|]+/u;

/**
 * Üye etiketi önerisi (§6.4 kalıp b, §6.1): çocuk etiketi üst etiketiyle başlıyorsa (büyük/küçük
 * harf duyarsız, TR) önek + ayırıcı soyulur, ilk harf büyütülür; önek bir sözcüğün parçasıysa ya
 * da geriye bir şey kalmazsa etiket aynen döner.
 */
export function stripParentPrefix(parentLabel: string, childLabel: string): string {
  const parent = parentLabel.trim();
  const child = childLabel.trim();
  if (parent === "") return child;
  if (!child.toLocaleLowerCase("tr").startsWith(parent.toLocaleLowerCase("tr"))) return child;
  const restRaw = child.slice(parent.length);
  if (restRaw !== "" && !SEPARATORS_RE.test(restRaw)) return child; // "Toplamda …" — sözcük devamı
  const rest = restRaw.replace(SEPARATORS_RE, "").trim();
  if (rest === "") return child;
  return rest.charAt(0).toLocaleUpperCase("tr") + rest.slice(1);
}

/** İfade tam olarak tek argümanlı `TOPLA(x)` ise `x`; değilse null (dönüştürme yeniden yazımı). */
export function exactSumRef(expression: string): string | null {
  const p = parseFormula(expression);
  if (!p.ok) return null;
  const ast = p.ast as { kind: string; code?: string };
  return ast.kind === "sum" && typeof ast.code === "string" ? ast.code : null;
}

/** R13: `%` ve oran (`a/b`) biriminde varsayılan toplama kuralı `none`; diğerlerinde `sum`. */
export function defaultBreakdownRollup(unit: string | null | undefined): KpiBreakdown["rollup"] {
  if (!unit) return "sum";
  const key = normalizeUnitKey(unit);
  return key === "%" || key.includes("/") ? "none" : "sum";
}
